import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PLATFORMS } from "@/data/platforms";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

const hashOf = (str) => [...str].reduce((a, c) => a + c.charCodeAt(0), 0);

const buildLogs = (domain) =>
  HE
    ? [
        { t: 300, text: `מאתרים את ${domain}`, out: "OK" },
        { t: 900, text: "מריצים ב-ChatGPT שאלות אמיתיות של לקוחות", out: "בוצע" },
        { t: 1500, text: "בודקים ציטוטים ב-Google AI Overviews", out: "בוצע" },
        { t: 2100, text: "סורקים את Gemini, Perplexity, Copilot ו-Claude", out: "6 מנועים" },
        { t: 6500, text: "מחשבים את ציון נראות ה-AI שלכם", out: "…" },
      ]
    : [
        { t: 300, text: `Resolving ${domain}`, out: "OK" },
        { t: 900, text: "Querying ChatGPT with real buyer prompts", out: "DONE" },
        { t: 1500, text: "Checking Google AI Overviews", out: "DONE" },
        { t: 2100, text: "Scanning Gemini, Perplexity, Copilot & Claude", out: "6 ENGINES" },
        { t: 6500, text: "Compiling your AI visibility score", out: "…" },
      ];

const TickUp = ({ to, delay, duration = 2600, className }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const id = setTimeout(() => {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => {
      clearTimeout(id);
      cancelAnimationFrame(raf);
    };
  }, [to, delay, duration]);
  return <span className={className} dir="ltr">{v.toLocaleString()}</span>;
};

const PlatformRow = ({ p, i, h }) => {
  const val = 22 + ((h * 7 + i * 37) % 63);
  const answers = 120 + ((h + i * 53) % 340);
  const startDelay = 2.3 + i * 0.45;
  const [scanned, setScanned] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setScanned(true), (startDelay + 1.4) * 1000);
    return () => clearTimeout(id);
  }, [startDelay]);

  const Icon = p.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: startDelay - 0.25 }}
      className="flex items-center gap-3 border-b border-white/10 py-2.5"
    >
      <Icon style={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
      <span className="font-data text-[11px] text-white w-[88px] shrink-0 truncate" dir="ltr">{p.short || p.name}</span>
      <div className="flex-1 h-2 bg-white/10 overflow-hidden">
        <motion.div
          className="h-2"
          style={{ background: p.color }}
          initial={{ width: 0 }}
          animate={{ width: `${val}%` }}
          transition={{ delay: startDelay, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <span className="font-data text-[10px] w-[92px] text-right shrink-0">
        {scanned ? (
          <span className="text-data-positive">✓ <span dir="ltr">{answers}</span> {t("analyzer.answers")}</span>
        ) : (
          <span className="text-neutral-500 animate-pulse">{t("analyzer.scanning")}</span>
        )}
      </span>
    </motion.div>
  );
};

export const AnalyzerOverlay = ({ domain, onDone }) => {
  const [logs, setLogs] = useState([]);
  const [pct, setPct] = useState(0);
  const fired = useRef(false);
  const h = hashOf(domain);

  useEffect(() => {
    const timers = buildLogs(domain).map((l) => setTimeout(() => setLogs((prev) => [...prev, l]), l.t));
    let raf;
    const start = performance.now();
    const TOTAL = 7300;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / TOTAL);
      setPct(Math.round(100 * (1 - Math.pow(1 - p, 2))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const fin = setTimeout(() => {
      if (!fired.current) {
        fired.current = true;
        onDone();
      }
    }, 7700);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(fin);
      cancelAnimationFrame(raf);
    };
  }, [domain, onDone]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-obsidian text-white overflow-y-auto"
      data-testid="analyzer-overlay"
    >
      {/* moving scanline */}
      <div className="absolute left-0 right-0 h-24 bg-gradient-to-b from-conversion/20 to-transparent av-scanline pointer-events-none" />

      <div className="min-h-full flex flex-col justify-center max-w-xl mx-auto px-5 py-10">
        {/* header */}
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.25em] text-neutral-400">
            <span className="h-2 w-2 bg-conversion animate-pulse" /> {t("analyzer.deep")}
          </span>
          <span className="font-data text-[11px] text-conversion truncate max-w-[45%]" dir="ltr" data-testid="scan-domain">{domain}</span>
        </div>

        {/* big percent */}
        <div className="flex items-end gap-3 mb-5">
          <span className="font-display text-6xl sm:text-7xl leading-none" dir="ltr" data-testid="scan-percent">{pct}%</span>
          <span className="font-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 pb-2">
            {t("analyzer.across1")}<br />{t("analyzer.across2")}
          </span>
        </div>

        {/* terminal log */}
        <div className="border border-white/15 bg-white/[0.03] p-3.5 mb-5 min-h-[118px]">
          {logs.map((l) => (
            <motion.div
              key={l.text}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between gap-3 font-data text-[11px] py-[3px]"
            >
              <span className="text-neutral-400 truncate">▸ {l.text}…</span>
              <span className={l.out === "…" ? "text-conversion animate-pulse" : "text-data-positive"}>{l.out}</span>
            </motion.div>
          ))}
        </div>

        {/* per-platform scan */}
        <div className="mb-5">
          <span className="font-data text-[10px] uppercase tracking-[0.25em] text-neutral-500">{t("analyzer.compare")} — <span dir="ltr">{domain}</span></span>
          <div className="mt-2">
            {PLATFORMS.map((p, i) => (
              <PlatformRow key={p.name} p={p} i={i} h={h} />
            ))}
          </div>
        </div>

        {/* live stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t("analyzer.kw"), to: 1284, delay: 2400 },
            { label: t("analyzer.ans"), to: 1842, delay: 2700 },
            { label: t("analyzer.cit"), to: 37, delay: 3000 },
          ].map((s) => (
            <div key={s.label} className="border border-white/15 p-3">
              <TickUp to={s.to} delay={s.delay} className="font-data text-xl sm:text-2xl font-bold text-white" />
              <div className="font-data text-[9px] uppercase tracking-[0.18em] text-neutral-500 mt-1 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-center font-data text-[10px] uppercase tracking-[0.22em] text-neutral-600">
          {t("analyzer.footer")}
        </p>
      </div>
    </motion.div>
  );
};

export default AnalyzerOverlay;
